import { ChildrenLabelMapper } from 'purespectrum-lib';
import { Constants as CreateSurveyConstants } from '../../shared/constants/create-survey.constants';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { Criteria } from '../../shared/interfaces/quota.interface';
import { SurveyQuotaCriterion } from './survey-quota-criterion';
import { NestingQualification } from '../../shared/interfaces/quota-nesting.interface';

interface DescriptionBuild {
  build(): string;
}

export class RangeCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }

  constructor(private readonly _criterion: Criteria) {}

  public build(): string {
    return this.criterion
      .range()
      .reduce(
        (labels, rangeSet) => [...labels, `${rangeSet.from}-${rangeSet.to}`],
        <string[]>[]
      )
      .join(',');
  }
}

export class ChildrenCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }
  constructor(private readonly _criterion: Criteria) {}

  public build(): string {
    if (
      this.criterion.conditions().length === 1 &&
      this.criterion.conditions()[0] ===
        CreateSurveyConstants.CHILDREN_CONDITION_CODES.NO_CHILDREN
    ) {
      return 'No Children';
    }

    if (
      this.criterion.qualificationCode() === QUALIFICATION_CODES.CHILD_GENDER &&
      this.criterion.conditions().length === 1
    ) {
      return this.criterion.conditions()[0] ===
        CreateSurveyConstants.CHILD_GENDER.BOY
        ? (CreateSurveyConstants.CHILD_GENDER_MAPPING[
            CreateSurveyConstants.CHILD_GENDER.BOY as keyof Object
          ] as unknown as string)
        : (CreateSurveyConstants.CHILD_GENDER_MAPPING[
            CreateSurveyConstants.CHILD_GENDER.GIRL as keyof Object
          ] as unknown as string);
    }

    if (
      this.criterion.qualificationCode() === QUALIFICATION_CODES.CHILD_GENDER
    ) {
      return CreateSurveyConstants.CHILD_GENDER_MAPPING[
        CreateSurveyConstants.CHILD_GENDER.BOTH as keyof Object
      ] as unknown as string;
    }

    return CreateSurveyConstants.UNKNOWN_CONDITION;
  }
}

export class CAGCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }

  constructor(private readonly _criterion: Criteria) {}

  public build(): string {
    return new ChildrenLabelMapper(
      this.criterion.conditions().map(Number),
      this.criterion.childAgeUnit()
    ).label();
  }
}

export class AdvancedCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }

  constructor(
    private readonly _criterion: Criteria,
    private readonly _selectedQualification: NestingQualification
  ) {}

  public build(): string {
    if (this._selectedQualification.selectedAnswers?.length) {
      const descriptions = this.criterion
        .conditions()
        .map((condition) => {
          const found = (
            this._selectedQualification.selectedAnswers || []
          ).find(
            (selectedAnswer) => Number(selectedAnswer.id) === Number(condition)
          );
          return found ? found.text : '';
        })
        .filter((text) => !!text);
      return descriptions.length
        ? descriptions.join('--')
        : CreateSurveyConstants.UNKNOWN_CONDITION;
    }

    if (this.criterion.conditions().length) {
      return new ConditionCriterionDescriptionBuilder(this._criterion).build();
    }

    return CreateSurveyConstants.UNKNOWN_CONDITION;
  }
}

export class LocationCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }

  constructor(
    private readonly _criterion: Criteria,
    private readonly _selectedQualification: NestingQualification
  ) {}

  public build(): string {
    const descriptions = this.criterion
      .conditions()
      .map((condition) => {
        const found = (this._selectedQualification?.data || []).find(
          (datum) => Number(datum.id) === Number(condition)
        );
        return found ? found.text || found.name : '';
      })
      .filter((text) => !!text);
    return descriptions.length
      ? descriptions.join('--')
      : CreateSurveyConstants.UNKNOWN_CONDITION;
  }
}

export class ConditionCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }

  constructor(private readonly _criterion: Criteria) {}

  public build(): string {
    return this.criterion.conditionNames().length
      ? this.criterion.conditionNames().join('--')
      : CreateSurveyConstants.UNKNOWN_CONDITION;
  }
}

export class ZipCodeCriterionDescriptionBuilder implements DescriptionBuild {
  public get criterion(): SurveyQuotaCriterion {
    return new SurveyQuotaCriterion(this._criterion);
  }

  constructor(private readonly _criterion: Criteria) {}

  public build(): string {
    return (
      this.criterion.groupName() || CreateSurveyConstants.UNKNOWN_CONDITION
    );
  }
}
